import { freeTextSchema, TEXT_MAX_LENGTH } from '@1trading/fast-api-domain/forms';
import { HTTPError } from 'ky';
import { z } from 'zod';

import { BINARY_ANSWERS } from '@/constants/answers';
import i18n from '@/lib/i18n';
import { fastApi } from '@/services/api';
import { blankAsAbsent } from '@/utils/blank-as-absent';
import { textErrors } from '@/utils/error-messages';

const PATH = 'v1/customer/form/aml';

export const AML_OTHER_ANSWER = 'OTHER' as const;
export const AML_EMPLOYMENT_STATUS_ANSWERS = ['EMPLOYED', 'SELF_EMPLOYED', 'RETIRED', 'STUDENT', 'NOT_EMPLOYED'] as const;
export const AML_MONETARY_ANSWERS = ['UP_TO_10k', 'FROM_10k_TO_20k', 'FROM_20k_TO_50k', 'FROM_50k_TO_100k', 'MORE_THAN_100k'] as const;
export const AML_PURPOSE_ANSWERS = ['CONVERSION', 'SAVING', 'SPECULATIVE', 'TRADING'] as const;
export const AML_PURPOSE_ANSWERS_WITH_OTHER = [...AML_PURPOSE_ANSWERS, AML_OTHER_ANSWER] as const;
export const AML_FUNDS_ORIGIN_ANSWERS = ['SALARY', 'LOAN', 'PENSION', 'INVESTMENTS_EARNINGS', 'INHERITANCE', 'SAVING'] as const;
export const AML_FUNDS_ORIGIN_ANSWERS_WITH_OTHER = [...AML_FUNDS_ORIGIN_ANSWERS, AML_OTHER_ANSWER] as const;

export const amlAnswersSchema = z
  .object({
    pep: z.enum(BINARY_ANSWERS, { error: i18n.t('requiredField.error') }),
    employmentStatus: z.enum(AML_EMPLOYMENT_STATUS_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    occupation: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('requiredField.error') }),
    annualNetIncome: z.enum(AML_MONETARY_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    investmentPerYear: z.enum(AML_MONETARY_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    withdrawPerMonth: z.enum(AML_MONETARY_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    depositFiatPerMonth: z.enum(AML_MONETARY_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    depositCryptoPerMonth: z.enum(AML_MONETARY_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    tradingVolume: z.enum(AML_MONETARY_ANSWERS, {
      error: i18n.t('requiredField.error'),
    }),
    purpose: z.enum(AML_PURPOSE_ANSWERS_WITH_OTHER, {
      error: i18n.t('requiredField.error'),
    }),
    purposeOther: blankAsAbsent(freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('requiredField.error') })),
    fundsOrigin: z.enum(AML_FUNDS_ORIGIN_ANSWERS_WITH_OTHER, {
      error: i18n.t('requiredField.error'),
    }),
    fundsOriginOther: blankAsAbsent(freeTextSchema({ maxLength: TEXT_MAX_LENGTH.MEDIUM, errors: textErrors('requiredField.error') })),
  })
  .refine((data) => data.purpose !== AML_OTHER_ANSWER || !!data.purposeOther, {
    message: i18n.t('requiredField.error'),
    path: ['purposeOther'],
  })
  .refine((data) => data.fundsOrigin !== AML_OTHER_ANSWER || !!data.fundsOriginOther, {
    message: i18n.t('requiredField.error'),
    path: ['fundsOriginOther'],
  });

export type AmlAnswers = z.infer<typeof amlAnswersSchema>;

export enum AmlRiskLevel {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  PROHIBITED = 4,
}

const amlSchema = z
  .object({
    answers: amlAnswersSchema,
  })
  .and(
    z.discriminatedUnion('result', [
      z.object({
        date: z.iso.datetime(),
        score: z.number(),
        riskLevel: z.enum(AmlRiskLevel),
      }),
      z.object({
        riskLevel: z.never().optional(),
      }),
    ]),
  );

type Aml = z.infer<typeof amlSchema>;

type AmlGetResponse = Aml;

type AmlPostRequestBody = Omit<Aml, 'result'>;

export const getAml = async () => {
  return fastApi
    .get(PATH)
    .json<AmlGetResponse>()
    .catch((error) => {
      if (error instanceof HTTPError && error.response.status === 404) return null;

      throw error;
    });
};

export const postAml = async (data: AmlPostRequestBody) => {
  return fastApi
    .post(PATH, {
      json: data,
    })
    .json<AmlGetResponse>();
};
